import { useId, useState } from 'react';
import { categoriesOf, refuseCategory } from '@/domain';
import { useCatalog } from '@/application/usecases/useIntake';
import { useCreateCatalogItem, useUpdateCatalogItem } from '@/application/usecases/useAdmin';
import type { CatalogItemView } from '@/application/ports/JobOrderRepository';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { SaidError, failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { formatAmount } from '@/shared/format/money';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { ResponsiveTable, type Column } from '@/ui/data/ResponsiveTable';
import { PlusIcon } from '@/ui/icons';
import { useConfirmAction } from '../shell/useConfirmAction';
import usersStyles from '../admin/UsersScreen.module.css';
import styles from './ItemsScreen.module.css';

type Kind = 'unit' | 'span';

/** The category select's last choice: name a new one. */
const NEW_CATEGORY = '\u0000new';

interface Draft {
  name: string;
  price: string;
}

function isConflict(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 409;
}

function fieldErrorsOf(error: unknown): Record<string, string[]> {
  return typeof error === 'object' && error !== null && 'fieldErrors' in error
    ? (error as { fieldErrors: Record<string, string[]> }).fieldErrors
    : {};
}

/** A price typed into a box, in two places, or null when it is not one. */
function parsePrice(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) / 100 : null;
}

/**
 * Items: the lab's price list. Section 2.5 of the system test handover, named
 * as the client asked for it.
 *
 * Add an item, rename it, change its price, retire it. Nothing is deleted: a
 * retired item stays on the cases that used it and in the reports, and sits
 * greyed at the foot of its category so a manager asking why it is on an old
 * invoice can find it. Intake no longer offers it.
 *
 * Each case keeps the price it was taken in at, so a price change here
 * applies to new cases only. A rename reaches every case carrying the item,
 * because the case holds the item's id rather than its name.
 *
 * Every one of those is asked about first, since the request of 5 October 2026
 * (third), section 2. What is typed is checked before the question, so a
 * refusal the screen can give itself never opens a sheet; the question says
 * what the change reaches (every case for a name, new cases for a price) and,
 * for a new item, its category and kind, which cannot be changed afterwards.
 * Cancel leaves the form and the row exactly as they were.
 */
export function ItemsScreen() {
  const catalog = useCatalog();
  const create = useCreateCatalogItem();
  const update = useUpdateCatalogItem();
  const ask = useConfirmAction();
  const ids = {
    heading: useId(),
    name: useId(),
    category: useId(),
    newCategory: useId(),
    price: useId(),
    kind: useId(),
  };

  const [name, setName] = useState('');
  const [category, setCategory] = useState<string>('Zircon');
  // A category is a word on an item, so a new one is named with its first item.
  const [newCategory, setNewCategory] = useState('');
  const [price, setPrice] = useState('');
  const [kind, setKind] = useState<Kind>('unit');
  const [addErrors, setAddErrors] = useState<Record<string, string[]>>({});
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  const items = catalog.data?.items ?? [];
  const categories = categoriesOf(catalog.data?.categories ?? items.map((item) => item.category));

  function add() {
    const local: Record<string, string[]> = {};
    const unitPrice = parsePrice(price);
    const naming = category === NEW_CATEGORY;
    const chosen = naming ? newCategory.trim() : category;
    if (name.trim() === '') local.name = [strings.items.nameRequired];
    if (unitPrice === null) local.unitPrice = [strings.items.priceInvalid];
    const refusal = naming ? refuseCategory(newCategory) : null;
    if (refusal !== null) local.category = [refusal];
    setAddErrors(local);
    if (Object.keys(local).length > 0 || unitPrice === null) return;

    const input = { name: name.trim(), category: chosen, unitPrice, kind };
    ask(
      strings.confirm.addItem(input.name, chosen, naming, formatAmount(unitPrice), kind === 'span'),
      () =>
        create.mutateAsync(input).then(
          (item) => {
            toast(`${item.name} ${strings.items.added}`);
            setName('');
            setPrice('');
            // The new heading is a heading now; the next item may go in it too.
            if (naming) {
              setCategory(item.category);
              setNewCategory('');
            }
          },
          (error: Error) => {
            // A refusal about a field goes back on the field; anything else is said by the confirmation.
            if (isConflict(error)) {
              setAddErrors({ name: [failureWords(error)] });
              return;
            }
            const fields = fieldErrorsOf(error);
            setAddErrors(fields);
            if (Object.keys(fields).length === 0) throw error;
          },
        ),
      { confirmLabel: strings.items.add },
    );
  }

  function draftFor(item: CatalogItemView): Draft {
    return (
      drafts[item.id] ?? {
        name: item.name,
        price: item.unitPrice === null || item.unitPrice === undefined ? '' : String(item.unitPrice),
      }
    );
  }

  function setDraft(item: CatalogItemView, patch: Partial<Draft>) {
    setDrafts((current) => ({ ...current, [item.id]: { ...draftFor(item), ...patch } }));
  }

  function forget(item: CatalogItemView) {
    setDrafts((current) => {
      const next = { ...current };
      delete next[item.id];
      return next;
    });
  }

  /** A row can be saved when something changed and what is typed is valid. */
  function changes(item: CatalogItemView): { name?: string; unitPrice?: number } | null {
    const draft = drafts[item.id];
    if (draft === undefined) return null;
    const nextName = draft.name.trim();
    const nextPrice = parsePrice(draft.price);
    if (nextName === '' || nextPrice === null) return null;
    const edit = {
      ...(nextName === item.name ? {} : { name: nextName }),
      ...(nextPrice === item.unitPrice ? {} : { unitPrice: nextPrice }),
    };
    return Object.keys(edit).length === 0 ? null : edit;
  }

  /** The question for a row's Save: what is renamed, what is repriced, and what each reaches. */
  function saveStatement(item: CatalogItemView, edit: { name?: string; unitPrice?: number }): string {
    const from = item.unitPrice === null || item.unitPrice === undefined ? null : formatAmount(item.unitPrice);
    if (edit.name === undefined) {
      return strings.confirm.repriceItem(item.name, from, formatAmount(edit.unitPrice ?? 0));
    }
    return edit.unitPrice === undefined
      ? strings.confirm.renameItem(item.name, edit.name)
      : strings.confirm.renameAndRepriceItem(item.name, edit.name, from, formatAmount(edit.unitPrice));
  }

  function save(item: CatalogItemView) {
    // What was asked is what is sent: the row as it stood when Save was pressed.
    const edit = changes(item);
    if (edit === null) return;
    ask(
      saveStatement(item, edit),
      () =>
        update.mutateAsync({ id: item.id, rowVersion: item.rowVersion, edit }).then(
          (saved) => {
            toast(`${saved.name} ${strings.items.saved}`);
            forget(item);
          },
          (error: Error) => {
            // Somebody else changed it: the row is reloaded, and the confirmation says why.
            if (isConflict(error)) {
              forget(item);
              if (error.message === '') throw new SaidError(strings.items.conflict);
            }
            throw error;
          },
        ),
      { confirmLabel: strings.items.save },
    );
  }

  function toggle(item: CatalogItemView) {
    ask(
      item.active ? strings.confirm.retireItem(item.name) : strings.confirm.restoreItem(item.name),
      () =>
        update
          .mutateAsync({ id: item.id, rowVersion: item.rowVersion, edit: { active: !item.active } })
          .then((saved) =>
            toast(
              saved.active
                ? `${saved.name} ${strings.items.restoredToast}`
                : `${saved.name} ${strings.items.retiredToast}`,
            ),
          ),
      { confirmLabel: item.active ? strings.items.retire : strings.items.restore },
    );
  }

  // The columns as this reader laid them out, the same for every category. The request of 7 October 2026, section 3.
  const layout = useTableLayout('items');

  const columns: Column<CatalogItemView>[] = [
    {
      key: 'name',
      label: strings.items.name,
      render: (item) =>
        item.active ? (
          <input
            className={styles.cellInput}
            aria-label={`${strings.items.nameFor} ${item.name}`}
            value={draftFor(item).name}
            maxLength={160}
            onChange={(event) => setDraft(item, { name: event.target.value })}
          />
        ) : (
          <span className={styles.retiredText}>{item.name}</span>
        ),
    },
    {
      key: 'price',
      label: strings.items.price,
      align: 'right',
      mono: true,
      render: (item) =>
        item.active ? (
          <input
            className={`${styles.cellInput} ${styles.price}`}
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            aria-label={`${strings.items.priceFor} ${item.name}`}
            value={draftFor(item).price}
            onChange={(event) => setDraft(item, { price: event.target.value })}
          />
        ) : (
          <span className={styles.retiredText}>{item.unitPrice ?? ''}</span>
        ),
    },
    {
      key: 'kind',
      label: strings.items.kind,
      render: (item) => (item.kind === 'span' ? strings.items.kindSpan : strings.items.kindUnit),
    },
    {
      key: 'status',
      label: strings.items.status,
      render: (item) =>
        item.active ? (
          strings.items.active
        ) : (
          <span className={styles.badge}>{strings.items.retired}</span>
        ),
    },
    {
      key: 'actions',
      label: strings.items.actions,
      render: (item) => (
        <span className={styles.actions}>
          {item.active && (
            <button
              type="button"
              className={usersStyles.mini}
              disabled={changes(item) === null || update.isPending}
              aria-label={`${strings.items.save} ${item.name}`}
              onClick={() => save(item)}
            >
              {strings.items.save}
            </button>
          )}
          <button
            type="button"
            className={usersStyles.mini}
            disabled={update.isPending}
            aria-label={`${item.active ? strings.items.retire : strings.items.restore} ${item.name}`}
            onClick={() => toggle(item)}
          >
            {item.active ? strings.items.retire : strings.items.restore}
          </button>
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className={usersStyles.head}>
        <span className={usersStyles.lbl}>{strings.items.label}</span>
        <h2>{strings.items.title}</h2>
        <span className={usersStyles.hint}>{strings.items.hint}</span>
      </div>

      <section className={styles.card} aria-labelledby={ids.heading}>
        <h3 id={ids.heading} className={styles.cardTitle}>
          {strings.items.addTitle}
        </h3>
        <div className={styles.addRow}>
          <div className={styles.field}>
            <label htmlFor={ids.name}>{strings.items.name}</label>
            <input
              id={ids.name}
              className={styles.input}
              value={name}
              maxLength={160}
              placeholder={strings.items.namePlaceholder}
              onChange={(event) => setName(event.target.value)}
            />
            {addErrors.name !== undefined && (
              <span className={styles.error}>{addErrors.name.join(' ')}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor={ids.category}>{strings.items.category}</label>
            <select
              id={ids.category}
              className={styles.input}
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              {categories.map((key) => (
                <option key={key}>{key}</option>
              ))}
              <option value={NEW_CATEGORY}>{strings.items.newCategory}</option>
            </select>
            {addErrors.category !== undefined && category !== NEW_CATEGORY && (
              <span className={styles.error}>{addErrors.category.join(' ')}</span>
            )}
          </div>

          {category === NEW_CATEGORY && (
            <div className={styles.field}>
              <label htmlFor={ids.newCategory}>{strings.items.newCategoryName}</label>
              <input
                id={ids.newCategory}
                className={styles.input}
                placeholder={strings.items.newCategoryPlaceholder}
                maxLength={64}
                autoFocus
                value={newCategory}
                onChange={(event) => setNewCategory(event.target.value)}
              />
              {addErrors.category !== undefined && (
                <span className={styles.error}>{addErrors.category.join(' ')}</span>
              )}
            </div>
          )}

          <div className={styles.field}>
            <label htmlFor={ids.price}>{strings.items.price}</label>
            <input
              id={ids.price}
              className={`${styles.input} ${styles.price}`}
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
            />
            {addErrors.unitPrice !== undefined && (
              <span className={styles.error}>{addErrors.unitPrice.join(' ')}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor={ids.kind}>{strings.items.kind}</label>
            <select
              id={ids.kind}
              className={styles.input}
              value={kind}
              onChange={(event) => setKind(event.target.value as Kind)}
            >
              <option value="unit">{strings.items.kindUnit}</option>
              <option value="span">{strings.items.kindSpan}</option>
            </select>
          </div>

          <div className={styles.addAction}>
            <Button variant="primary" icon={<PlusIcon />} disabled={create.isPending} onClick={add}>
              {create.isPending ? strings.items.adding : strings.items.add}
            </Button>
          </div>
        </div>
      </section>

      {catalog.isError && <p className={usersStyles.hint}>{strings.errors.server}</p>}

      {catalog.isPending ? (
        <SkeletonRows rows={6} label={strings.items.loading} />
      ) : (
        categories.map((key, index) => {
          // In use first, retired greyed at the foot. Stable, so each half
          // keeps the price list's own order.
          const rows = items
            .filter((item) => item.category === key)
            .sort((a, b) => Number(b.active) - Number(a.active));
          return (
            <section key={key} className={styles.category}>
              <h3 className={styles.categoryTitle}>{key}</h3>
              <ResponsiveTable
                columns={columns}
                rows={rows}
                rowKey={(item) => item.id}
                caption={`${strings.items.caption} ${key}`}
                emptyHeading={strings.items.empty}
                // One control for the whole price list, over its first category.
                customize={{ layout: layout.layout, save: layout.save, reset: layout.reset, quiet: index > 0 }}
              />
            </section>
          );
        })
      )}
    </div>
  );
}
